import React, { useState, useMemo } from 'react';
import { Button, TextField, Grid, styled, Icon, Typography, CircularProgress, IconButton } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Clear } from '@mui/icons-material';
import UploadIcon from '@mui/icons-material/Upload';
import Card from '@mui/material/Card';
import { createUser } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../contexts/authContext';
import { debounce } from 'lodash';
import { validateEmail } from '../../../services/apiservice';

const RedAsterisk = styled('span')({
  color: 'red',
});

const FileInputWrapper = styled('label')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
});

const UploadInput = styled('input')({
  display: 'none',
});

const UploadText = styled('span')(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const ClickableText = styled(Typography)({
  cursor: 'pointer',
  color: 'blue',
  textDecoration: 'underline',
});

const ImageNameWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

function UserForm() {
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [imageName, setImageName] = useState(null);
  const [emailValidationError, setEmailValidationError] = useState(null);
  const { user } = useAuth();
  const UserID = user.UserID;

  const validationSchema = Yup.object({
    UserFirstName: Yup.string().required('First Name is required'),
    UserLastName: Yup.string().required('Last Name is required'),
    UserEmail: Yup.string().email('Invalid email address').required('Email is required'),
    // UserEmail: Yup.string()
    // .email('Invalid email address')
    // .required('Email is required')
    // .test('email-validation', 'Invalid email address. Please provide a valid email.', function(value) {
    //   return !emailValidationError;
    // }),
    UserPhoneNumber: Yup.string().nullable(),
    UserCommunicationEmail: Yup.string().email('Invalid email address').nullable(),
    UserCompanyEmail: Yup.string().email('Invalid email address').nullable(),
    UserJobTitle: Yup.string().nullable(),
    UserCompanyName: Yup.string().nullable(),
    UserJobIndustry: Yup.string().nullable(),
    ProfileImageUrl: Yup.mixed().nullable()
      .test(
        'fileSize',
        'Image size is too large, max size is 2MB',
        value => !value || (value && value.size <= 2 * 1024 * 1024)
      )
      .test(
        'fileType',
        'Only image formats are allowed (jpg, png, jpeg)',
        value => !value || (value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type))
      ),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClearImage = (setFieldValue) => {
    setImageName(null);
    setFieldValue('ProfileImageUrl', null);
  };

  // const debouncedCheckEmailValidation = useMemo(
  //   () =>
  //     debounce(async (email) => {
  //       if (!email) {
  //         setEmailValidationError(null);
  //         return;
  //       }
  //       try {
  //         const response = await validateEmail(email);
  //         if (!response) {
  //           setEmailValidationError('Invalid email address. Please provide a valid email.');
  //         } else {
  //           setEmailValidationError(null);
  //         }
  //       } catch (error) {
  //         console.error('Error checking email validation:', error);
  //       }
  //     }, 300),
  //   [setEmailValidationError]
  // );

  return (
    <>
      <Helmet>
        <title>User Form</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Formik
        initialValues={{
          UserFirstName: '',
          UserLastName: '',
          UserEmail: '',
          UserPhoneNumber: null,
          UserCommunicationEmail: null,
          UserJobTitle: null,
          UserCompanyEmail: null,
          UserCompanyName: null,
          UserJobIndustry: null,
          ProfileImageUrl: null,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const formData = new FormData();
            Object.keys(values).forEach(key => {
              const value = values[key];
              if (value !== '' && value !== null) {
                formData.append(key, value);
              }
            });

            formData.append('UserCreatorID', UserID);

            const response = await createUser(formData);
            if (response.success) {
              setSnackbarMessage('User created successfully!');
              setSnackbarSeverity('success');
              resetForm();
              setImageName('');
            } else {
              setSnackbarMessage(response.message || 'Failed to create User.');
              setSnackbarSeverity('error');
            }
          } catch (error) {
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
          } finally {
            setSnackbarOpen(true);
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, setFieldValue, isSubmitting }) => (
          <Card sx={{ width: '100%', padding: 2 }}>
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={<>First Name <RedAsterisk>*</RedAsterisk></>}
                    name="UserFirstName"
                    fullWidth
                    error={touched.UserFirstName && Boolean(errors.UserFirstName)}
                    helperText={touched.UserFirstName && errors.UserFirstName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={<>Last Name <RedAsterisk>*</RedAsterisk></>}
                    name="UserLastName"
                    fullWidth
                    error={touched.UserLastName && Boolean(errors.UserLastName)}
                    helperText={touched.UserLastName && errors.UserLastName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={<>Email <RedAsterisk>*</RedAsterisk></>}
                    name="UserEmail"
                    fullWidth
                    error={touched.UserEmail && Boolean(errors.UserEmail) || emailValidationError}
                    helperText={touched.UserEmail && errors.UserEmail || emailValidationError}
                    onChange={(e) => {
                      const email = e.target.value;
                      setFieldValue('UserEmail', email);
                      // const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                      // isCompleteEmail && debouncedCheckEmailValidation(email);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label="Phone Number"
                    name="UserPhoneNumber"
                    fullWidth
                    error={touched.UserPhoneNumber && Boolean(errors.UserPhoneNumber)}
                    helperText={touched.UserPhoneNumber && errors.UserPhoneNumber}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FileInputWrapper>
                    <UploadInput
                      accept="image/*"
                      id="ProfileImageUrl"
                      name="ProfileImageUrl"
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setFieldValue("ProfileImageUrl", file);
                        setImageName(file ? file.name : null);
                      }}
                    />
                    <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                      Upload Profile Image
                    </Button>
                  </FileInputWrapper>
                  <UploadText style={{ marginLeft: '8px' }}>
                    {imageName ? (
                      <>
                        {imageName}
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClearImage(setFieldValue);
                          }}
                          size="small"
                          color="error"
                          aria-label="clear"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      'No file chosen'
                    )}
                  </UploadText>
                  {errors.ProfileImageUrl && (
                    <Typography color="error">{errors.ProfileImageUrl}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ClickableText onClick={() => setShowMoreDetails(!showMoreDetails)}>
                    {showMoreDetails ? 'Hide More Details' : 'Add More Details'}
                  </ClickableText>
                </Grid>
                {showMoreDetails && (
                  <>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label="Job Title ( Optional )"
                        name="UserJobTitle"
                        fullWidth
                        error={touched.UserJobTitle && Boolean(errors.UserJobTitle)}
                        helperText={touched.UserJobTitle && errors.UserJobTitle}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label="Company Name ( Optional )"
                        name="UserCompanyName"
                        fullWidth
                        error={touched.UserCompanyName && Boolean(errors.UserCompanyName)}
                        helperText={touched.UserCompanyName && errors.UserCompanyName}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label="Communication Email ( Optional )"
                        name="UserCommunicationEmail"
                        fullWidth
                        error={touched.UserCommunicationEmail && Boolean(errors.UserCommunicationEmail)}
                        helperText={touched.UserCommunicationEmail && errors.UserCommunicationEmail}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label="Job Industry ( Optional )"
                        name="UserJobIndustry"
                        fullWidth
                        error={touched.UserJobIndustry && Boolean(errors.UserJobIndustry)}
                        helperText={touched.UserJobIndustry && errors.UserJobIndustry}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label="Company Email ( Optional )"
                        name="UserCompanyEmail"
                        fullWidth
                        error={touched.UserCompanyEmail && Boolean(errors.UserCompanyEmail)}
                        helperText={touched.UserCompanyEmail && errors.UserCompanyEmail}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                  >
                    {isSubmitting ? 'Creating User...' : 'Create User'}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Card>
        )}
      </Formik>
    </>
  );
}

export default UserForm;
